/** Cross-tab sync for global notification snooze (`muted_until`) and quiet hours. */

export const SNOOZE_STORAGE_KEY = 'fms.notification.snooze.muted_until'
export const QUIET_HOURS_STORAGE_KEY = 'fms.notification.quiet_hours'
export const SNOOZE_BROADCAST_CHANNEL = 'fms.notification.snooze'

export type SnoozeSyncMessage = {
  type: 'snooze'
  /** ISO timestamp or null when snooze is cleared / expired. */
  muted_until: string | null
}

export type QuietHoursSyncPayload = {
  enabled: boolean
  start: string | null
  end: string | null
  timezone: string
}

export type QuietHoursSyncMessage = {
  type: 'quiet-hours'
} & QuietHoursSyncPayload

export type NotificationQuietSyncMessage = SnoozeSyncMessage | QuietHoursSyncMessage

/** Active snooze end time, or null if unset / already past. */
export function activeMutedUntil(iso: string | null | undefined, nowMs = Date.now()): Date | null {
  if (!iso) {
    return null
  }
  const d = new Date(iso)
  if (Number.isNaN(d.getTime()) || d.getTime() <= nowMs) {
    return null
  }
  return d
}

/** Floor snooze deadline to the selected minute (seconds / ms → 0). */
export function snoozeUntilAtMinute(value: Date): Date {
  const next = new Date(value.getTime())
  next.setSeconds(0, 0)
  return next
}

export function readSnoozeFromStorage(): string | null {
  try {
    return localStorage.getItem(SNOOZE_STORAGE_KEY)
  } catch {
    return null
  }
}

export function writeSnoozeToStorage(iso: string | null): void {
  try {
    if (iso) {
      localStorage.setItem(SNOOZE_STORAGE_KEY, iso)
    } else {
      localStorage.removeItem(SNOOZE_STORAGE_KEY)
    }
  } catch {
    /* private mode / quota */
  }
}

export function publishSnoozeMutedUntil(iso: string | null): void {
  writeSnoozeToStorage(iso)
  try {
    if (typeof BroadcastChannel === 'undefined') {
      // storage event still notifies other same-origin tabs
      return
    }
    const channel = new BroadcastChannel(SNOOZE_BROADCAST_CHANNEL)
    const message: SnoozeSyncMessage = { type: 'snooze', muted_until: iso }
    channel.postMessage(message)
    channel.close()
  } catch {
    /* ignore */
  }
}

export function readQuietHoursFromStorage(): QuietHoursSyncPayload | null {
  try {
    const raw = localStorage.getItem(QUIET_HOURS_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<QuietHoursSyncPayload>
    if (!parsed || typeof parsed !== 'object') return null
    return {
      enabled: Boolean(parsed.enabled),
      start: parsed.start ?? null,
      end: parsed.end ?? null,
      timezone: (parsed.timezone || 'UTC').trim() || 'UTC',
    }
  } catch {
    return null
  }
}

export function writeQuietHoursToStorage(payload: QuietHoursSyncPayload | null): void {
  try {
    if (!payload || !payload.enabled) {
      localStorage.removeItem(QUIET_HOURS_STORAGE_KEY)
      return
    }
    localStorage.setItem(QUIET_HOURS_STORAGE_KEY, JSON.stringify(payload))
  } catch {
    /* private mode / quota */
  }
}

export function publishQuietHours(payload: QuietHoursSyncPayload, options?: { broadcast?: boolean }): void {
  writeQuietHoursToStorage(payload.enabled ? payload : null)
  if (options?.broadcast === false) {
    return
  }
  try {
    if (typeof BroadcastChannel === 'undefined') {
      return
    }
    const channel = new BroadcastChannel(SNOOZE_BROADCAST_CHANNEL)
    const message: QuietHoursSyncMessage = { type: 'quiet-hours', ...payload }
    channel.postMessage(message)
    channel.close()
  } catch {
    /* ignore */
  }
}
